import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sprout, ShieldCheck, Sparkles } from "lucide-react";

const CATS = ["All", "Ideas", "Recipes", "Lifestyle", "Faith"];

export default function TryCritique() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = category !== "All" ? { category } : {};
    api.get("/try-and-critique", { params })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <main className="max-w-6xl mx-auto px-6 pt-12 pb-24">
      <span className="accent-label text-[#B79256]">separate pool · disclosed by design</span>
      <h1 className="font-serif-display text-5xl sm:text-6xl text-[#2A2823] mt-3 leading-none">Try &amp; Critique</h1>
      <p className="text-[#2A2823]/70 mt-4 max-w-2xl leading-relaxed">
        A dedicated pool of products that brands have paid a flat fee to be tested on Willoah.
        None of these ever mix into The Grove, get ranked, or earn a placement — the flat fee only buys the chance to be honestly reviewed.
      </p>

      {/* Rules of the road */}
      <section className="mt-10 grid md:grid-cols-3 gap-6">
        {[
          { icon: ShieldCheck, title: "Brands never see reviews first.", body: "No approvals, no previews, no way to bury a critical Field Note." },
          { icon: Sparkles, title: "Automatic disclosure.", body: `Every review lands with the proof label "Gifted to me" — locked and non-editable.` },
          { icon: Sprout, title: "No boosted placement.", body: "These branches sort exactly like organic ones — never lifted, never dimmed." },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="border-l-2 border-[#B79256]/50 pl-5">
            <Icon className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.5} />
            <h3 className="font-serif-display text-xl text-[#4E5A3D] mt-2">{title}</h3>
            <p className="text-sm text-[#2A2823]/70 mt-2 leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="flex flex-wrap gap-2" data-testid="tc-category-filters">
          {CATS.map((c) => {
            const active = category === c;
            return (
              <button
                key={c}
                data-testid={`tc-filter-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`accent-label px-4 py-2 rounded-full border transition-colors duration-200 ${
                  active ? "bg-[#B79256] text-[#FBF8F1] border-[#B79256]" : "bg-transparent text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/10"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
        <Button asChild variant="outline" className="rounded-full h-11 px-5 border-[#B79256]/50 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]">
          <Link data-testid="apply-brand-link" to="/apply">Are you a brand? Apply →</Link>
        </Button>
      </div>

      {loading ? (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-3xl bg-white border border-[#B79256]/20 h-[360px] animate-pulse"/>)}
        </div>
      ) : products.length === 0 ? (
        <div data-testid="tc-empty" className="mt-12 text-center py-24 border border-dashed border-[#B79256]/40 rounded-3xl">
          <p className="font-serif-display text-3xl text-[#4E5A3D]">Nothing to try just yet.</p>
          <p className="text-[#2A2823]/60 mt-3">Check back — brands will land here after the site owner approves them.</p>
        </div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="tc-products">
          {products.map((p) => (
            <Link key={p.id} to={`/try-and-critique/${p.id}`} data-testid={`tc-card-${p.id}`} className="group block">
              <div className="overflow-hidden rounded-3xl bg-white border border-[#B79256]/20 shadow-[0_10px_40px_rgb(42,40,35,0.06)] transition-transform duration-300 ease-out group-hover:-translate-y-1">
                <div className="w-full aspect-[4/5] bg-[#B79256]/5">
                  <img src={FILE_URL(p.photo_path)} alt={p.product_name} className="w-full h-full object-cover"/>
                </div>
                <div className="p-5">
                  <div className="accent-label text-[#B79256]">{p.brand_name} · {p.category}</div>
                  <h3 className="font-serif-display text-2xl text-[#2A2823] leading-tight mt-1">{p.product_name}</h3>
                  <p className="text-sm text-[#2A2823]/60 mt-3 line-clamp-2">{p.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
